import { CustomEventWithDetail, html, View } from 'rune-ts';
import { ButtonAction } from '../../../../shared/components/atoms/ButtonAction/ButtonAction';
import { font } from '../../../../shared/typography';
import { WikiRestoreIcon } from '../../../components/atoms/Icon';
import { Modal, ModalClosedEvent } from '../../../components/atoms/Modal/Modal';
import klass from '../../../components/cells/ProductFilterModal/ProductFilterModal.module.scss';
import button_klass from './CreatorShopMembershipFilterModal.module.scss';
import { ProductFilterButtonList } from '../../../components/cells/ProductFilterModal/views/ProductFilterButtonList';
import { CreatorShopMembershipProductListPageData } from '../CreatorShopMembershipProductListPage';

export interface CreatorShopMembershipFilterModalData {
  is_mobile: boolean;
}

export interface CreatorShopMembershipFilter {
  memberships: CreatorShopMembershipProductListPageData['memberships'];
}

class ResetButton extends ButtonAction {}

class ConfirmButton extends ButtonAction {}

export class CreatorShopMembershipFilterChangedEvent extends CustomEventWithDetail<CreatorShopMembershipFilter> {}
export class CreatorShopMembershipFilterConfirmEvent extends CustomEventWithDetail<CreatorShopMembershipFilter> {}

class CreatorShopMembershipFilterBody extends View<CreatorShopMembershipFilterModalData> {
  MembershipFilter: ProductFilterButtonList;
  constructor(
    data: CreatorShopMembershipFilterModalData,
    public filter: CreatorShopMembershipFilter,
  ) {
    super(data);

    this.MembershipFilter = new ProductFilterButtonList(
      this.filter.memberships.map((m) => ({ ...m, label: m.name })),
      this.filter.memberships.filter((m) => m.selected).map((m) => m.id),
      {
        klass: button_klass.button_container,
        btn_klass: button_klass.button,
      },
    );
  }

  protected override template() {
    return html`<form class="${klass.filter}">
      <div class="${klass.filter_container}">
        <span class="${klass.title} ${font('14_medium')}">${ET('mps2::membership::plan')}</span>
        ${this.MembershipFilter}
      </div>
    </form>`;
  }

  protected override onRender() {
    this.delegate('click', ProductFilterButtonList, () => {
      this.dispatchEvent(CreatorShopMembershipFilterChangedEvent, {
        bubbles: true,
        detail: this.getValue(),
      });
    });
  }

  clear() {
    this.MembershipFilter.clear();
  }

  getValue(): CreatorShopMembershipFilter {
    return {
      memberships: this.filter.memberships.map((m) => ({
        ...m,
        selected: this.MembershipFilter.selected.has(m.id),
      })),
    };
  }

  isValueSelected(): boolean {
    return !!this.filter.memberships.filter((m) => m.selected).length;
  }

  setValue(filter: CreatorShopMembershipFilter) {
    this.filter = filter;
    this.MembershipFilter.setSelected(filter.memberships.filter((m) => m.selected).map((m) => m.id));
  }
}

export class CreatorShopMembershipFilterModal extends View<CreatorShopMembershipFilterModalData> {
  FilterBody: CreatorShopMembershipFilterBody;
  ResetButton: ButtonAction;
  ConfirmButton: ButtonAction;
  FilterModal: Modal;

  constructor(
    data: CreatorShopMembershipFilterModalData,
    public filter: CreatorShopMembershipFilter,
  ) {
    super(data);

    this.FilterBody = new CreatorShopMembershipFilterBody(this.data, filter);

    this.ResetButton = new ResetButton({
      label: this.data.is_mobile ? html`${WikiRestoreIcon}` : ET('mps2::common::reset'),
      size: 'large',
      klass: klass.reset,
      type: this.FilterBody.isValueSelected() ? 'white' : 'disabled',
    });

    this.ConfirmButton = new ConfirmButton({
      label: ET('mps2::product::filter::confirm'),
      size: 'large',
      klass: klass.confirm,
      type: 'disabled',
    });

    this.FilterModal = new Modal({
      title: ET('mps2::product::filter::title'),
      size: this.data.is_mobile ? 'large' : 'medium',
      isOpen: false,
      backdrop: true,
      body: this.FilterBody,
      footer: html`${this.ResetButton}${this.ConfirmButton}`,
      container_klass: klass.product_filter_modal,
      enable_app_bottom_padding: true,
    });
  }

  override template() {
    return html`<div>${this.FilterModal}</div> `;
  }

  override onRender() {
    this.delegate(ModalClosedEvent<{ is_confirmed: boolean } | undefined>, Modal, (e) => {
      const is_confirmed = e.detail?.is_confirmed;
      if (!is_confirmed) {
        this.undo();
      }
    });

    this.delegate('click', ResetButton, () => {
      const value = this.clear();
      this.dispatchEvent(CreatorShopMembershipFilterConfirmEvent, {
        bubbles: true,
        detail: value,
      });
      this.close();
    });

    this.delegate('click', ConfirmButton, () => {
      this.dispatchEvent(CreatorShopMembershipFilterConfirmEvent, {
        bubbles: true,
        detail: this.FilterBody.getValue(),
      });
      this.FilterModal.close({ is_confirmed: true });
    });

    this.delegate(CreatorShopMembershipFilterChangedEvent, CreatorShopMembershipFilterBody, () => {
      this.changeButtonActivation();
    });
  }

  open() {
    this.FilterModal.open();
  }

  close() {
    this.FilterModal.close({ is_confirmed: true });
  }

  clear(): CreatorShopMembershipFilter {
    this.FilterBody.clear();
    this.FilterBody.redraw();
    this.save();
    this.changeButtonActivation();
    return this.FilterBody.getValue();
  }

  undo() {
    this.updateFilter(this.filter);
  }

  updateFilter(filter: CreatorShopMembershipFilter) {
    this.FilterBody.setValue(filter);
    this.FilterBody.redraw();
    this.filter = filter;
    this.changeButtonActivation();
  }

  save() {
    this.filter = this.FilterBody.getValue();
  }

  private isChanged() {
    function compareArrays<T = any>(arr1: T[], arr2: T[]): boolean {
      if (arr1.length !== arr2.length) {
        return false;
      }

      for (const element of arr1) {
        if (!arr2.includes(element)) {
          return false;
        }
      }

      for (const element of arr2) {
        if (!arr1.includes(element)) {
          return false;
        }
      }

      return true;
    }

    function compareArraysWithoutDuplicates<T = any>(arr1: T[], arr2: T[]) {
      const unique_arr1 = Array.from(new Set(arr1));
      const unique_arr2 = Array.from(new Set(arr2));

      // 두 배열 비교
      return compareArrays(unique_arr1, unique_arr2);
    }

    const current = this.FilterBody.getValue();
    const not_changed = compareArraysWithoutDuplicates(
      current.memberships.filter((m) => m.selected).map((m) => m.id),
      this.filter.memberships.filter((m) => m.selected).map((m) => m.id),
    );

    return !not_changed;
  }

  private changeButtonActivation() {
    this._changeResetButtonActivation();
    this._changeConfirmButtonActivation();
  }

  private _changeResetButtonActivation() {
    if (this.FilterBody.isValueSelected()) {
      this.ResetButton.data.type = 'white';
    } else {
      this.ResetButton.data.type = 'disabled';
    }
    this.ResetButton.redraw();
  }

  private _changeConfirmButtonActivation() {
    this.ConfirmButton.data.type = this.isChanged() ? 'blue' : 'disabled';
    this.ConfirmButton.redraw();
  }
}
